import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  makeStyles,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TestCaseInstanceFields } from '../instance-review/TestCaseInstanceFields';
import type { TestCaseInstance } from '../instance-review/types';
import type { TestCaseThreadStatus } from '../types';
import { ForceApproveWarningDialog } from './ForceApproveWarningDialog';
import { ThreadActionConfirmationDialog } from './ThreadActionConfirmationDialog';
import {
  approveTestCaseThread,
  deleteTestCaseThread,
  forceApproveTestCaseThread,
  startTestCaseThread,
} from './threadReviewApi';
import type { ForceApproveWarning, ThreadReviewAction } from './types';

interface ThreadReviewSectionProps {
  threadId: string;
  status: TestCaseThreadStatus;
  current: TestCaseInstance | null;
  onStatusChange: (status: TestCaseThreadStatus) => void;
  onDeleted: () => void;
  onBusyChange?: (busy: boolean) => void;
}

type ConfirmationAction = 'approve' | 'forceApprove' | 'delete' | null;

const useStyles = makeStyles(theme => ({
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
  },
  contentCard: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
  },
  deleteButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
}));

export const ThreadReviewSection = ({
  threadId,
  status,
  current,
  onStatusChange,
  onDeleted,
  onBusyChange,
}: ThreadReviewSectionProps) => {
  const classes = useStyles();
  const { t } = useTranslation('testCases');

  const [processing, setProcessing] = useState(false);
  const [currentAction, setCurrentAction] = useState<ThreadReviewAction | null>(
    null,
  );

  const [confirmationAction, setConfirmationAction] =
    useState<ConfirmationAction>(null);

  const [warnings, setWarnings] = useState<ForceApproveWarning[]>([]);

  const [checkedInstanceId, setCheckedInstanceId] = useState<string | null>(
    null,
  );

  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  const [errorOpen, setErrorOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const runAction = async (
    action: ThreadReviewAction,
    operation: () => Promise<void>,
    successMessageKey: string,
  ) => {
    if (processing) {
      return;
    }

    setProcessing(true);
    setCurrentAction(action);
    setErrorOpen(false);
    onBusyChange?.(true);

    try {
      await operation();
      setSuccessMessage(t(successMessageKey));
    } catch {
      setErrorOpen(true);
    } finally {
      setProcessing(false);
      setCurrentAction(null);
      onBusyChange?.(false);
    }
  };

  const handleStartWorking = () =>
    runAction(
      'start',
      async () => {
        await startTestCaseThread(threadId);
        onStatusChange('WIP');
      },
      'testCaseGeneration.threadReview.messages.started',
    );

  const handleApprove = () =>
    runAction(
      'approve',
      async () => {
        await approveTestCaseThread(threadId);
        setConfirmationAction(null);
        onStatusChange('APPROVED');
      },
      'testCaseGeneration.threadReview.messages.approved',
    );

  const handleForceApprove = async () => {
    if (processing) {
      return;
    }

    setProcessing(true);
    setCurrentAction('forceApprove');
    setErrorOpen(false);
    onBusyChange?.(true);

    try {
      const result = await forceApproveTestCaseThread(threadId);

      if (!result.approved) {
        if (!result.checkedInstanceId) {
          throw new Error(
            'Force approval response did not contain checked instance ID.',
          );
        }

        setWarnings(result.warnings);
        setCheckedInstanceId(result.checkedInstanceId);
        setWarningDialogOpen(true);
        return;
      }

      setWarnings([]);
      setCheckedInstanceId(null);
      onStatusChange('APPROVED');
      setSuccessMessage(
        t('testCaseGeneration.threadReview.messages.forceApproved'),
      );
    } catch {
      setErrorOpen(true);
    } finally {
      setProcessing(false);
      setCurrentAction(null);
      onBusyChange?.(false);
    }
  };

  const handleApproveAnyway = () =>
    runAction(
      'forceApprove',
      async () => {
        if (!checkedInstanceId) {
          throw new Error('Force approval requires the checked instance ID.');
        }

        const result = await forceApproveTestCaseThread(
          threadId,
          checkedInstanceId,
        );

        if (!result.approved) {
          setWarnings(result.warnings);
          setCheckedInstanceId(result.checkedInstanceId || null);
          throw new Error('Force approval did not approve the thread.');
        }

        setWarningDialogOpen(false);
        setWarnings([]);
        setCheckedInstanceId(null);
        onStatusChange('APPROVED');
      },
      'testCaseGeneration.threadReview.messages.forceApproved',
    );

  const handleDelete = () =>
    runAction(
      'delete',
      async () => {
        await deleteTestCaseThread(threadId);
        setConfirmationAction(null);
        onDeleted();
      },
      'testCaseGeneration.threadReview.messages.deleted',
    );

  const isReadOnly = status === 'APPROVED' || status === 'DELETED';

  return (
    <>
      <Box className={classes.statusRow}>
        <Box>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t('testCaseGeneration.threadReview.currentTestCase')}
          </Typography>

          <Typography variant="body2" color="textSecondary">
            {t('testCaseGeneration.threadReview.currentTestCaseDescription')}
          </Typography>
        </Box>

        <Chip
          color={status === 'APPROVED' ? 'primary' : 'default'}
          label={t(`testCaseGeneration.threadReview.status.${status}`)}
        />
      </Box>

      {isReadOnly && (
        <Box mb={2}>
          <Alert severity="info">
            {status === 'APPROVED'
              ? t('testCaseGeneration.threadReview.approvedReadOnly')
              : t('testCaseGeneration.threadReview.deletedReadOnly')}
          </Alert>
        </Box>
      )}

      <Card variant="outlined" className={classes.contentCard}>
        <CardContent>
          {current ? (
            <>
              <Box mb={2}>
                <Typography
                  variant="subtitle1"
                  className={classes.sectionTitle}
                >
                  {t('testCaseGeneration.threadReview.currentVersion', {
                    version: current.versionNumber,
                  })}
                </Typography>
              </Box>

              <TestCaseInstanceFields fields={current.fields} />
            </>
          ) : (
            <Alert severity="info">
              {t('testCaseGeneration.threadReview.noCurrent')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {errorOpen && (
        <Alert severity="error">
          {t('testCaseGeneration.threadReview.messages.actionFailed')}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {!isReadOnly && (
        <Box className={classes.actions}>
          {(status === 'DRAFT' || status === 'WIP') && (
            <Button
              className={classes.deleteButton}
              variant="outlined"
              disabled={processing}
              onClick={() => setConfirmationAction('delete')}
            >
              {t('testCaseGeneration.threadReview.delete')}
            </Button>
          )}

          {status === 'DRAFT' && (
            <Button
              color="primary"
              variant="contained"
              disabled={processing}
              onClick={handleStartWorking}
              startIcon={
                currentAction === 'start' ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {currentAction === 'start'
                ? t('testCaseGeneration.threadReview.starting')
                : t('testCaseGeneration.threadReview.startWorking')}
            </Button>
          )}

          {status === 'WIP' && (
            <>
              <Button
                color="primary"
                variant="outlined"
                disabled={processing}
                onClick={handleForceApprove}
                startIcon={
                  currentAction === 'forceApprove' ? (
                    <CircularProgress size={16} />
                  ) : undefined
                }
              >
                {t('testCaseGeneration.threadReview.forceApprove')}
              </Button>

              <Button
                color="primary"
                variant="contained"
                disabled={processing}
                onClick={() => setConfirmationAction('approve')}
              >
                {t('testCaseGeneration.threadReview.approve')}
              </Button>
            </>
          )}
        </Box>
      )}

      <ThreadActionConfirmationDialog
        open={confirmationAction === 'approve'}
        title={t('testCaseGeneration.threadReview.approveDialog.title')}
        message={t('testCaseGeneration.threadReview.approveDialog.message')}
        confirmLabel={t('testCaseGeneration.threadReview.approve')}
        cancelLabel={t('testCaseGeneration.threadReview.cancel')}
        processing={processing}
        onCancel={() => setConfirmationAction(null)}
        onConfirm={handleApprove}
      />

      <ThreadActionConfirmationDialog
        open={confirmationAction === 'delete'}
        title={t('testCaseGeneration.threadReview.deleteDialog.title')}
        message={t('testCaseGeneration.threadReview.deleteDialog.message')}
        confirmLabel={t('testCaseGeneration.threadReview.delete')}
        cancelLabel={t('testCaseGeneration.threadReview.cancel')}
        processing={processing}
        destructive
        onCancel={() => setConfirmationAction(null)}
        onConfirm={handleDelete}
      />

      <ForceApproveWarningDialog
        open={warningDialogOpen}
        title={t('testCaseGeneration.threadReview.forceApproveDialog.title')}
        description={t(
          'testCaseGeneration.threadReview.forceApproveDialog.description',
        )}
        cancelLabel={t('testCaseGeneration.threadReview.cancel')}
        approveAnywayLabel={t('testCaseGeneration.threadReview.approveAnyway')}
        warnings={warnings}
        processing={processing}
        onCancel={() => {
          setWarningDialogOpen(false);
          setWarnings([]);
          setCheckedInstanceId(null);
        }}
        onApproveAnyway={handleApproveAnyway}
      />
    </>
  );
};

export default ThreadReviewSection;
